if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // En desktop Dawn muestra una sola imagen activa en las galerías con
        // miniaturas. Por eso las flechas del slider no tienen un área que
        // desplazar: las conectamos directamente a la foto anterior/siguiente.
        this.elements.viewer
          .querySelectorAll('.slider-buttons .slider-button')
          .forEach((button) => button.addEventListener('click', this.onDesktopArrowClick.bind(this), true));

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onDesktopArrowClick(event) {
        if (!this.mql.matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (mediaItems.length < 2) return;

        const activeIndex = Math.max(
          0,
          mediaItems.findIndex((item) => item.classList.contains('is-active'))
        );
        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;

        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/*
 * Desktop PDP fallback.
 *
 * The Peacock desktop gallery only displays the `.is-active` media item. Dawn's
 * generic slider arrows move `scrollLeft`, so they can appear clickable without
 * changing the visible item. Handle those two controls at document level so the
 * navigation remains reliable even when the media-gallery element was upgraded
 * before its thumbnail slider was ready.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const arrow = event.target.closest(
      '.product__media-wrapper slider-component[id^="GalleryViewer-"] > .slider-buttons .slider-button'
    );
    if (!arrow) return;

    const viewer = arrow.closest('slider-component[id^="GalleryViewer-"]');
    const gallery = arrow.closest('media-gallery');
    const mediaList = viewer?.querySelector('[id^="Slider-Gallery-"]');
    if (!viewer || !gallery || !mediaList) return;

    const mediaItems = Array.from(mediaList.children).filter((item) => item.matches('[data-media-id]'));
    if (mediaItems.length < 2) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const activeIndex = Math.max(
      0,
      mediaItems.findIndex((item) => item.classList.contains('is-active'))
    );
    const direction = arrow.name === 'next' ? 1 : -1;
    const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
    const nextMedia = mediaItems[nextIndex];

    mediaItems.forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-hidden', 'true');
    });
    nextMedia.classList.add('is-active');
    nextMedia.removeAttribute('aria-hidden');

    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (thumbnails) {
      thumbnails.querySelectorAll('button[aria-current]').forEach((button) => button.removeAttribute('aria-current'));
      const nextThumbnail = thumbnails.querySelector(`[data-target="${nextMedia.dataset.mediaId}"] button`);
      nextThumbnail?.setAttribute('aria-current', 'true');
      nextThumbnail?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    const currentCounter = viewer.querySelector('.slider-counter--current');
    if (currentCounter) currentCounter.textContent = String(nextIndex + 1);

    window.pauseAllMedia?.();
    const deferredMedia = nextMedia.querySelector('.deferred-media');
    deferredMedia?.loadContent?.(false);
  },
  true
);
